import React, { useState, useEffect } from 'react';
import { Row, Col, Select, Table, Space, Card, Modal, DatePicker, message, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { getDashboardData, updateLoanData } from '../services/dashboardService';
import { getConsultantBillingLoans } from '../services/loanService';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, formatPercentage } from '../utils/formatters';
import Page from '../components/layout/Page';
import MetricCard from '../components/MetricCard';
import { Link, useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';

const { Option } = Select;

const Dashboard = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [primaryConsultantLoans, setPrimaryConsultantLoans] = useState([]);
    const [temporaryConsultantLoans, setTemporaryConsultantLoans] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [isSignatureModalVisible, setIsSignatureModalVisible] = useState(false);
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [signatureDate, setSignatureDate] = useState(null);
    const navigate = useNavigate();

    const getCommissionTierDisplay = (loan) => {
        if (!loan.commission_tiers?.length) return '-';
        return loan.commission_tiers.map(tier => 
            `${tier.percentage}%`
        ).join(' ');
    };


    useEffect(() => {
        fetchDashboardData();
        fetchConsultantLoans();
    }, [selectedYear]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const data = await getDashboardData(selectedYear);
            console.log('Dashboard data:', data);
            setDashboardData(data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchConsultantLoans = async () => {
        try {
            setLoading(true);
            const data = await getConsultantBillingLoans(user.id);
            console.log('Consultant loans:', data);
            
            // Trennen der Kredite nach Beratertyp
            setPrimaryConsultantLoans(data.filter(loan => 
                loan.primary_consultant?.id === user.id && 
                loan.consultant_billable_status !== 'withdrawal'
            ) || []);
            setTemporaryConsultantLoans(data.filter(loan => loan.secondary_consultant?.id === user.id) || []);
        } catch (error) {
            console.error('Error fetching consultant loans:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddSignature = async () => {
        if (!signatureDate) {
            message.error(t('dashboard.signatures.dateRequired'));
            return;
        }

        try {
            setLoading(true);
            await updateLoanData(selectedLoan.id, {
                signature_date: signatureDate.format('YYYY-MM-DD'),
                status: 'signed'
            });
            message.success(t('dashboard.signatures.success'));
            setIsSignatureModalVisible(false);
            setSelectedLoan(null);
            setSignatureDate(null);
            await fetchDashboardData();
        } catch (error) {
            console.error('Error adding signature:', error);
            message.error(t('dashboard.signatures.error'));
        } finally {
            setLoading(false);
        }
    };

    const showSignatureModal = (loan) => {
        setSelectedLoan(loan);
        setIsSignatureModalVisible(true);
    };

    const yearSelect = (
        <Select
            value={selectedYear}
            onChange={setSelectedYear}
            style={{ width: 120 }}
        >
            {Array.from(new Array(5), (val, index) => (
                <Option key={index} value={new Date().getFullYear() - index}>
                    {new Date().getFullYear() - index}
                </Option>
            ))}
        </Select>
    );

    // Pending Signatures Table Columns
    const signatureColumns = [
        {
            title: t('loan.fields.customer'),
            dataIndex: 'customer',
            key: 'customer',
            width: 250,
            render: (customer) => `${customer.name} ${customer.first_name ? `, ${customer.first_name}` : ''}`,
        },
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },          
        {
            title: t('loan.fields.bank'),
            dataIndex: ['bank', 'name'],
            key: 'bank_name',
            width: 150,
        },            
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            width: 150,
            render: (amount) => formatCurrency(amount),
        },
        {
            title: t('loan.fields.provision'),
            dataIndex: 'provision',
            key: 'provision',
            align: 'right',
            width: 50,
            render: (amount) => formatPercentage(amount),
        },        
        {
            title: t('loan.fields.reportedAt'),
            dataIndex: 'reported_at',
            key: 'reported_at',
            render: (date) => formatDate(date),
        },
        {
            title: t('loan.fields.evaluationAmount'),
            dataIndex: 'evaluation_amount',
            key: 'evaluation_amount',
            render: (amount) => formatCurrency(amount || 0),
        },
        {
            title: t('common.actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        type="primary"
                        onClick={() => showSignatureModal(record)}
                    >
                        {t('dashboard.signatures.addSignature')}
                    </Button>
                </Space>
            ),
        },
    ];

    const CommissionTrendTable = ({ data }) => {
        const { t } = useTranslation();
        
        const germanMonths = [
            'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
            'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
        ];

        const columns = [
            {
                title: t('common.month'),
                dataIndex: 'month',
                key: 'month',
            },
            {
                title: t('dashboard.charts.evaluationAmount'),
                dataIndex: 'evaluation_amount',
                key: 'evaluation_amount',
                align: 'right',
                width: 150,
                render: (value) => formatCurrency(value)
            },
            {
                title: t('dashboard.charts.primaryCommission'),
                dataIndex: 'primary_commission',
                key: 'primary_commission',
                align: 'right',
                render: (value) => formatCurrency(value)
            },
            {
                title: t('dashboard.charts.extraBonus'),
                dataIndex: 'extra_bonus',
                key: 'extra_bonus',
                align: 'right',
                render: (value) => formatCurrency(value)
            },            
            {
                title: t('dashboard.charts.secondaryCommission'),
                dataIndex: 'secondary_commission',
                key: 'secondary_commission',
                align: 'right',
                render: (value) => formatCurrency(value)
            },
            {
                title: t('dashboard.charts.totalCommission'),
                dataIndex: 'total_commission',
                key: 'total_commission',
                align: 'right',
                render: (value) => formatCurrency(value)
            }
        ];

        const tableData = data?.map(item => ({
            key: item.month,
            month: germanMonths[item.month - 1],
            evaluation_amount: item.evaluation_amount,
            primary_commission: item.primary_commission,
            extra_bonus: item.extra_bonus,
            secondary_commission: item.secondary_commission,
            total_commission: item.total_commission
        })) || [];

        // Calculate sums for the summary row
        const calculateSummary = () => {
            // Only calculate summary if there is data
            if (!tableData || tableData.length === 0) {
                return null;
            }

            const sums = tableData.reduce((acc, curr) => ({
                evaluation_amount: (acc.evaluation_amount || 0) + (Number(curr.evaluation_amount) || 0),
                primary_commission: (acc.primary_commission || 0) + (Number(curr.primary_commission) || 0),
                extra_bonus: (acc.extra_bonus || 0) + (Number(curr.extra_bonus) || 0),
                secondary_commission: (acc.secondary_commission || 0) + (Number(curr.secondary_commission) || 0),
                total_commission: (acc.total_commission || 0) + (Number(curr.total_commission) || 0)
            }), {});

            // Format function that handles null/undefined/NaN
            const formatValue = (value) => {
                return isNaN(value) || value === null || value === undefined ? 
                    formatCurrency(0) : formatCurrency(value);
            };

            return (
                <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                    <Table.Summary.Cell index={0}>{t('common.total')}</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                        {formatValue(sums.evaluation_amount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                        {formatValue(sums.primary_commission)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                        {formatValue(sums.extra_bonus)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                        {formatValue(sums.secondary_commission)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                        {formatValue(sums.total_commission)}
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            );
        };

        return (
            <Table 
                columns={columns} 
                dataSource={tableData}
                pagination={false}
                size="small"
                summary={calculateSummary}
            />
        );
    };


    const headerExtra = (
        <Space>
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/loans/new')}
            >
                {t('loan.addLoan')}
            </Button>
        </Space>
    );
    return (
        <Page
            title={t('dashboard.title')}
            subtitle={t('dashboard.subtitle')}
            headerExtra={headerExtra}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Metrics */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                    <MetricCard
                        title={t('dashboard.metrics.total')}
                        value={formatCurrency(
                            Number(dashboardData?.in_withdrawal?.primary_consultant_share || 0) +
                            Number(dashboardData?.withdrawal_passed?.primary_consultant_share || 0) +
                            Number(dashboardData?.payment_confirmed?.primary_consultant_share || 0) + 
                            Number(dashboardData?.paid?.primary_consultant_share || 0) +
                            Number(dashboardData?.secondary_consultant_paid?.temporary_consultant_share || 0)
                        )}
                        loading={loading}
                    />
                    </Col>                    
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <MetricCard
                            title={t('dashboard.metrics.in_withdrawal')}
                            value={formatCurrency(dashboardData?.in_withdrawal.primary_consultant_share || 0)}
                            loading={loading}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <MetricCard
                            title={t('dashboard.metrics.withdrawal_passed')}
                            value={formatCurrency(
                                Number(dashboardData?.withdrawal_passed?.primary_consultant_share || 0) + 
                                Number(dashboardData?.payment_confirmed?.primary_consultant_share || 0)
                            )}
                            loading={loading}
                        />
                    </Col>                    
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <MetricCard
                            title={t('dashboard.metrics.paid')}
                            value={formatCurrency(
                                Number(dashboardData?.paid?.primary_consultant_share || 0) + 
                                Number(dashboardData?.secondary_consultant_paid?.temporary_consultant_share || 0)
                            )}
                            loading={loading}
                        />
                    </Col>                 
                </Row>

                {/* Pending Signatures Table */}
                <Card title={t('dashboard.signatures.title')}>
                    <div style={{ 
                        width: '100%',
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch', // For smooth scrolling on iOS
                    }}>
                        <Table
                            columns={signatureColumns}
                            dataSource={dashboardData?.pending_signatures || []}
                            loading={loading}
                            rowKey="id"
                            pagination={{ pageSize: 5 }}
                            scroll={{ x: 'max-content' }}
                        />
                    </div>
                </Card>

                <Row gutter={[24, 24]}>
                    <Col xs={24} sm={24} md={24} lg={24}>
                        {/* Primary Consultant Loans */}
                        <Card 
                            title={t('dashboard.primaryConsultant.title')}
                        >
                            <div style={{ 
                                width: '100%',
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch',
                            }}>
                                <Table
                                    columns={[
                                        {
                                            title: t('loan.fields.customer'),
                                            key: 'customer',
                                            width: 250,
                                            render: (_, record) => record.customer?.name || ''
                                        },
                                        {
                                            title: t('loan.fields.processNumber'),
                                            dataIndex: 'process_number',
                                            key: 'process_number',
                                            width: 150,
                                            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
                                        },
                                        {
                                            title: t('loan.fields.bank'),
                                            dataIndex: ['bank', 'name'],
                                            key: 'bank_name',
                                            width: 150,
                                        },                                      
                                        {
                                            title: t('loan.fields.amount'),
                                            dataIndex: 'amount',
                                            key: 'amount',
                                            align: 'right',
                                            width: 150,
                                            render: (amount) => formatCurrency(amount)
                                        }, 
                                        {
                                            title: t('loan.fields.commission'),
                                            dataIndex: 'provision',
                                            key: 'provision',
                                            align: 'right',

                                            render: (text) => `${parseFloat(text).toFixed(3)}%`,
                                            sorter: (a, b) => a.provision - b.provision
                                        },   
                                        {
                                            title: t('loan.fields.evaluationAmount'),
                                            dataIndex: 'evaluation_amount',
                                            key: 'evaluation_amount',
                                            align: 'right',

                                            render: (text) => formatCurrency(text),
                                            sorter: (a, b) => a.evaluation_amount - b.evaluation_amount
                                        },                                        
                                        {
                                            title: t('loan.fields.commissionTier'),
                                            dataIndex: 'commission_tiers',
                                            key: 'commission_tiers',
                                            align: 'right',

                                            render: (text, record) => getCommissionTierDisplay(record)
                                        },                                    
                                        {
                                            title: t('loan.fields.extraBonus'),
                                            key: 'extra_bonus',
                                            align: 'right',

                                            render: (_, record) => record.commission_info?.extra_bonus ? 
                                                formatCurrency(record.commission_info.extra_bonus) : '-'
                                        },          
                                        {
                                            title: t('loan.fields.primary_share'),
                                            key: 'commission',
                                            align: 'right',

                                            render: (_, record) => record.commission_info?.primary_share ? 
                                                formatCurrency(record.commission_info.primary_share) : '-'
                                        }
                                    ]}
                                    dataSource={primaryConsultantLoans}
                                    loading={loading}
                                    rowKey="id"
                                    pagination={{ pageSize: 5 }}
                                    scroll={{ x: 'max-content' }}
                                />
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={24}>
                        <Card title={t('dashboard.temporaryConsultant.title')}>
                            <div style={{ 
                                width: '100%',
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch',
                            }}>
                                <Table
                                    columns={[
                                        {
                                            title: t('loan.fields.customer'),
                                            key: 'customer',
                                            width: 250,

                                            render: (_, record) => record.customer?.name || ''
                                        },
                                        {
                                            title: t('loan.fields.processNumber'),
                                            dataIndex: 'process_number',
                                            key: 'process_number',
                                            width: 150,
                                            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
                                        },
                                        {
                                            title: t('loan.fields.bank'),
                                            dataIndex: ['bank', 'name'],
                                            key: 'bank_name',
                                            width: 150,
                                        },                                        
                                        {
                                            title: t('loan.fields.amount'),
                                            dataIndex: 'amount',
                                            key: 'amount',
                                            align: 'right',
                                            width: 150,
                                            render: (amount) => formatCurrency(amount)
                                        },
                                        {
                                            title: t('loan.fields.primaryConsultant'),
                                            key: 'primary_consultant',
                                            render: (_, record) => record.primary_consultant?.first_name + ' ' + record.primary_consultant?.last_name || ''
                                        },                                    
                                        {
                                            title: t('loan.fields.temporary_share'),
                                            key: 'commission',
                                            align: 'right',
                                            render: (_, record) => record.commission_info?.temporary_share ? 
                                                formatCurrency(record.commission_info.temporary_share) : '-'
                                        }
                                    ]}
                                    dataSource={temporaryConsultantLoans}
                                    loading={loading}
                                    rowKey="id"
                                    pagination={{ pageSize: 5 }}
                                    scroll={{ x: 'max-content' }}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Commission Trend Table */}
                <Card 
                    title={t('dashboard.charts.title')}
                    extra={yearSelect}
                >
                    <div style={{ 
                        width: '100%',
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                    }}>
                        <CommissionTrendTable data={dashboardData?.monthly_commission || []} />
                    </div>
                </Card>

            </Space>
            <Modal
                title={t('dashboard.signatures.modalTitle')}
                open={isSignatureModalVisible}
                onOk={handleAddSignature}
                onCancel={() => {
                    setIsSignatureModalVisible(false);
                    setSelectedLoan(null);
                    setSignatureDate(null);
                }}
                confirmLoading={loading}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                        <strong>{t('loan.fields.customer')}:</strong>{' '}
                        {selectedLoan?.customer?.name}
                    </div>
                    <div>
                        <strong>{t('loan.fields.amount')}:</strong>{' '}
                        {selectedLoan && formatCurrency(selectedLoan.amount)}
                    </div>
                    <div>
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder={t('dashboard.signatures.selectDate')}
                            onChange={setSignatureDate}
                            value={signatureDate}
                        />
                    </div>
                </Space>
            </Modal>
        </Page>
    );
};

export default Dashboard;
